import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@naf-simba/elements-buttons';
import { resolveLink } from '@lib/resolveLinkProps';
import { toCurrentSiteUrl } from '@lib/siteHosts';
import { LinkType } from '../../../../../types/LinkType';
import { useDocumentUrlWithNode } from '../../../hooks/Documents/useDocumentUrl';
import { useTrackCTA } from '../../../utils/Tracking/CtaClick/TrackCtaClick';

const CTAButton = ({ cta, baseUrl }: { cta: LinkType; baseUrl: string }) => {
  const navigate = useNavigate();

  const trackCTA = useTrackCTA();
  const ctaTarget = useDocumentUrlWithNode(cta?.internalReference);

  const handleCTAButtonClick = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>,
    ctaType: 'externalTarget' | 'internalReference' | 'relativeUrl',
    ctaUrl: string,
    ctaTitle: string,
  ) => {
    e.preventDefault();

    if (ctaType === 'externalTarget') {
      const linkUrlObject = new URL(ctaUrl);
      trackCTA({
        cta_position: 'content',
        text: ctaTitle,
        url: ctaUrl,
        cta_hostname: linkUrlObject.hostname,
      });
      window.open(toCurrentSiteUrl(ctaUrl), '_blank');
    } else if (ctaType === 'internalReference') {
      trackCTA({
        cta_position: 'content',
        text: ctaTitle,
        url: ctaUrl,
      });
      navigate(ctaUrl);
    } else if (ctaType === 'relativeUrl') {
      const { isInternal, linkProps } = resolveLink({ url: ctaUrl, baseUrl });
      if (isInternal) {
        trackCTA({
          cta_position: 'content',
          text: ctaTitle,
          url: linkProps.to,
        });
        navigate(linkProps.to);
      } else {
        const linkUrlObject = new URL(ctaUrl);
        trackCTA({
          cta_position: 'content',
          text: ctaTitle,
          url: ctaUrl,
          cta_hostname: linkUrlObject.hostname,
        });
        window.open(toCurrentSiteUrl(ctaUrl), '_blank');
      }
    }
  };

  if (cta?.externalTarget?.href) {
    return (
      <Button
        href={cta.externalTarget.href}
        target={cta.externalTarget.blank ? '_blank' : '_self'}
        onClick={(e) => handleCTAButtonClick(e, 'externalTarget', cta.externalTarget.href, cta.title)}
      >
        {cta.title}
      </Button>
    );
  }

  if (cta?.internalReference) {
    return (
      <Link to={ctaTarget} onClick={(e) => handleCTAButtonClick(e, 'internalReference', ctaTarget, cta.title ?? '')}>
        <Button type="button">{cta.title}</Button>
      </Link>
    );
  }

  if (cta?.relativeUrl?.url) {
    return (
      <Button onClick={(e) => handleCTAButtonClick(e, 'relativeUrl', cta.relativeUrl.url, cta.title)}>
        {cta.title}
      </Button>
    );
  }

  return null;
};

export default CTAButton;
